"use client";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { useBillingUIUpgrades } from "@/lib/flags/use-billing-ui-upgrades";
import { IconSidebarClose, IconSidebarOpen } from "@teral/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@teral/ui";
import { cn } from "cn";
import { TopNavFeedbackButton } from "../top-nav/feedback-button";
import { HelpButton } from "../top-nav/help-button";
import { UserButton } from "../top-nav/user-button";
import { ContextPath } from "./context-path";
import { SidebarBody } from "./sidebar-body";
import { UsageCard } from "./usage-card";

export const SIDEBAR_WIDTH_VARS: React.CSSProperties & {
  "--sidebar-width": string;
  "--sidebar-width-icon": string;
} = {
  "--sidebar-width": "15rem",
  "--sidebar-width-icon": "3rem",
};

type Props = React.ComponentProps<typeof Sidebar>;

export function SidebarV2(props: Props) {
  const { isMobile } = useSidebar();
  const billingUpgrades = useBillingUIUpgrades();
  if (isMobile) {
    return null;
  }
  return (
    <Sidebar
      {...props}
      collapsible="icon"
      // absolute, not the default viewport-fixed: the layout's relative content
      // row already starts below the top nav and the paused banner.
      className={cn("absolute h-auto [&_[data-sidebar=sidebar]]:bg-sunken", props.className)}
    >
      <SidebarHeader className="gap-0 p-0">
        <ContextPath />
      </SidebarHeader>
      <SidebarContent>
        <div className="mx-4 mt-1 h-px bg-grayA-4 group-data-[collapsible=icon]:mx-2" />
        <SidebarBody />
      </SidebarContent>
      <SidebarFooter className="mx-0 gap-2 border-t-0 p-2 pb-3">
        {billingUpgrades ? <UsageCard /> : null}
        <div className="flex items-center gap-0.5 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-1">
          <UserButton variant="rail" />
          <TopNavFeedbackButton tooltipSide="top" />
          <HelpButton variant="rail" />
          <CollapseButton />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

function CollapseButton() {
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";
  const Icon = collapsed ? IconSidebarOpen : IconSidebarClose;
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={label}
            className="flex size-8 items-center justify-center rounded-md text-gray-10 transition-colors hover:bg-grayA-3 hover:text-gray-12 outline-hidden focus-visible:outline-2 focus-visible:outline-(--focus-ring)"
          >
            <Icon className="size-4 shrink-0" />
          </button>
        }
      />
      <TooltipContent side="right" align="center">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
